import { useCallback, useEffect, useRef, useState } from 'react';
import { Bot, MessageCircle, Pencil, Send, X, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import { Pagination } from '../components/Pagination';
import {
  basalamService,
  type BasalamConversationRow,
  type BasalamMessageRow,
  type BasalamReadStatus,
} from '../services/basalamService';
import { formatDateTime } from '../utils/form';
import { rowNumber } from '../utils/rowNumber';

export function BasalamMessagesPage() {
  const [status, setStatus] = useState<BasalamReadStatus>('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState<BasalamConversationRow[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selected, setSelected] = useState<BasalamConversationRow | null>(null);
  const [messages, setMessages] = useState<BasalamMessageRow[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [autoReplyEnabled, setAutoReplyEnabled] = useState(true);
  const [autoReplyAllowlist, setAutoReplyAllowlist] = useState<string[]>([]);
  const [autoReplyLoading, setAutoReplyLoading] = useState(true);
  const [autoReplySaving, setAutoReplySaving] = useState(false);
  const threadRef = useRef<HTMLDivElement>(null);
  const limit = 15;

  const loadConversations = useCallback(async () => {
    setLoading(true);
    try {
      const res = await basalamService.listConversations({ page, limit, status });
      setConversations(res.data ?? []);
      setTotalPages(Math.max(1, res.totalPages || 1));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در دریافت گفتگوها');
      setConversations([]);
    } finally {
      setLoading(false);
    }
  }, [page, status]);

  const loadMessages = useCallback(async (conversationId: string) => {
    setMessagesLoading(true);
    try {
      const res = await basalamService.listMessages(conversationId, { page: 1, limit: 200 });
      setMessages(res.data ?? []);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در دریافت پیام‌ها');
      setMessages([]);
    } finally {
      setMessagesLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadConversations();
  }, [loadConversations]);

  const loadAutoReplySettings = useCallback(async () => {
    setAutoReplyLoading(true);
    try {
      const settings = await basalamService.getAutoReplySettings();
      setAutoReplyEnabled(settings.enabled);
      setAutoReplyAllowlist(settings.allowlist);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در دریافت تنظیمات پاسخ خودکار');
    } finally {
      setAutoReplyLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadAutoReplySettings();
  }, [loadAutoReplySettings]);

  const toggleAutoReply = async () => {
    setAutoReplySaving(true);
    try {
      const settings = await basalamService.setAutoReplyEnabled(!autoReplyEnabled);
      setAutoReplyEnabled(settings.enabled);
      setAutoReplyAllowlist(settings.allowlist);
      toast.success(settings.enabled ? 'پاسخ خودکار فعال شد' : 'پاسخ خودکار غیرفعال شد');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در ذخیره تنظیمات');
    } finally {
      setAutoReplySaving(false);
    }
  };

  useEffect(() => {
    if (!selectedId) {
      setSelected(null);
      setMessages([]);
      return;
    }
    void (async () => {
      try {
        const conv = await basalamService.getConversation(selectedId);
        setSelected(conv);
        await loadMessages(selectedId);
      } catch (e) {
        toast.error(e instanceof Error ? e.message : 'خطا');
        setSelectedId(null);
      }
    })();
  }, [selectedId, loadMessages]);

  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [messages]);

  const cancelEdit = () => {
    setEditingId(null);
    setEditText('');
  };

  const openConversation = (row: BasalamConversationRow) => {
    setSelectedId(row.id);
    setReplyText('');
    cancelEdit();
  };

  const sendReply = async () => {
    if (!selectedId || !replyText.trim()) return;
    setSending(true);
    try {
      const sent = await basalamService.reply(selectedId, replyText.trim());
      setMessages((prev) => [...prev, sent]);
      setReplyText('');
      setConversations((prev) =>
        prev.map((c) =>
          c.id === selectedId
            ? { ...c, lastMessageText: sent.text, lastMessageAt: sent.createdAt || new Date().toISOString() }
            : c
        )
      );
      toast.success('پاسخ ارسال شد');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در ارسال');
    } finally {
      setSending(false);
    }
  };

  const startEdit = (msg: BasalamMessageRow) => {
    setEditingId(msg.id);
    setEditText(msg.text);
  };

  const saveEdit = async () => {
    if (!selectedId || !editingId || !editText.trim()) return;
    setSavingEdit(true);
    try {
      const updated = await basalamService.editMessage(selectedId, editingId, editText.trim());
      setMessages((prev) => prev.map((m) => (m.id === editingId ? updated : m)));
      cancelEdit();
      toast.success('پیام ویرایش شد');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در ویرایش');
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="page-title text-xl font-bold text-slate-900 sm:text-2xl">پیام‌های باسلام</h1>
          <p className="mt-1 text-sm text-slate-500">
            همه گفتگوها از باسلام می‌آیند. پاسخ خودکار فقط برای {autoReplyAllowlist.join('، ') || 'لیست مجاز'} است.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => void toggleAutoReply()}
            disabled={autoReplyLoading || autoReplySaving}
            className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-medium transition-colors disabled:opacity-60 ${
              autoReplyEnabled
                ? 'border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100'
                : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
            }`}
            title={
              autoReplyAllowlist.length > 0
                ? `فقط برای: ${autoReplyAllowlist.join('، ')}`
                : 'لیست مشتریان مجاز از env خوانده می‌شود'
            }
          >
            <Bot className="h-4 w-4" />
            پاسخ خودکار: {autoReplyLoading ? '…' : autoReplyEnabled ? 'فعال' : 'غیرفعال'}
          </button>
          <div className="flex rounded-xl border border-slate-200 bg-white p-1">
          {([
            ['', 'همه'],
            ['unread', 'خوانده‌نشده'],
            ['read', 'خوانده‌شده'],
          ] as const).map(([value, label]) => (
            <button
              key={value || 'all'}
              type="button"
              onClick={() => {
                setStatus(value);
                setPage(1);
              }}
              className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                status === value ? 'bg-emerald-600 text-white' : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              {label}
            </button>
          ))}
          </div>
        </div>
      </div>

      <div className="grid min-h-[32rem] gap-4 lg:grid-cols-5">
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm lg:col-span-2">
          <div className="border-b border-slate-100 px-4 py-3 text-sm font-semibold text-slate-700">گفتگوها</div>
          <div className="admin-table-scroll max-h-[28rem] overflow-y-auto">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 bg-slate-50 text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-right font-semibold">#</th>
                  <th className="px-3 py-2 text-right font-semibold">مشتری</th>
                  <th className="px-3 py-2 text-right font-semibold">آخرین پیام</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={3} className="px-4 py-8 text-center text-slate-500">
                      در حال بارگذاری…
                    </td>
                  </tr>
                ) : conversations.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-4 py-8 text-center text-slate-500">
                      گفتگویی یافت نشد
                    </td>
                  </tr>
                ) : (
                  conversations.map((row, idx) => (
                    <tr
                      key={row.id}
                      onClick={() => openConversation(row)}
                      className={`cursor-pointer border-t border-slate-100 transition-colors hover:bg-emerald-50/50 ${
                        selectedId === row.id ? 'bg-emerald-50' : ''
                      }`}
                    >
                      <td className="px-3 py-3 text-slate-500">{rowNumber(page, limit, idx)}</td>
                      <td className="px-3 py-3">
                        <div className="font-medium text-slate-900">{row.displayName}</div>
                        {row.unreadCount > 0 ? (
                          <span className="mt-1 inline-block rounded-full bg-emerald-600 px-2 py-0.5 text-xs text-white">
                            {row.unreadCount} جدید
                          </span>
                        ) : null}
                      </td>
                      <td className="px-3 py-3">
                        <div className="line-clamp-2 text-slate-600">{row.lastMessageText || '—'}</div>
                        <div className="mt-1 text-xs text-slate-400">{formatDateTime(row.lastMessageAt)}</div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="border-t border-slate-100 p-3">
            <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
          </div>
        </div>

        <div className="flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm lg:col-span-3">
          {!selected ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-slate-500">
              <MessageCircle className="h-12 w-12 text-slate-300" />
              <p>یک گفتگو را انتخاب کنید</p>
            </div>
          ) : (
            <>
              <div className="border-b border-slate-100 px-4 py-3">
                <div className="font-semibold text-slate-900">{selected.displayName}</div>
              </div>

              <div ref={threadRef} className="flex-1 space-y-3 overflow-y-auto p-4" style={{ maxHeight: '22rem' }}>
                {messagesLoading ? (
                  <div className="py-8 text-center text-slate-500">در حال بارگذاری پیام‌ها…</div>
                ) : messages.length === 0 ? (
                  <div className="py-8 text-center text-slate-500">پیامی نیست</div>
                ) : (
                  messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.direction === 'out' ? 'justify-start' : 'justify-end'}`}
                    >
                      <div
                        className={`group relative max-w-[85%] rounded-2xl px-4 py-2 text-sm ${
                          msg.direction === 'out'
                            ? 'bg-emerald-600 text-white'
                            : 'bg-slate-100 text-slate-800'
                        }`}
                      >
                        {editingId === msg.id ? (
                          <div className="space-y-2">
                            <textarea
                              value={editText}
                              onChange={(e) => setEditText(e.target.value)}
                              rows={3}
                              className="w-full resize-none rounded-lg border border-emerald-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-emerald-400 focus:outline-none"
                            />
                            <div className="flex justify-end gap-2">
                              <button type="button" onClick={cancelEdit} className="inline-flex items-center gap-1 rounded-lg bg-white/20 px-2 py-1 text-xs hover:bg-white/30">
                                <X className="h-3 w-3" />
                                انصراف
                              </button>
                              <button
                                type="button"
                                onClick={() => void saveEdit()}
                                disabled={savingEdit || !editText.trim()}
                                className="inline-flex items-center gap-1 rounded-lg bg-white px-2 py-1 text-xs text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                              >
                                <Check className="h-3 w-3" />
                                ذخیره
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <p className="whitespace-pre-wrap break-words">{msg.text || `[${msg.contentType}]`}</p>
                            <div className="mt-1 flex items-center justify-between gap-3">
                              <div className={`text-xs ${msg.direction === 'out' ? 'text-emerald-100' : 'text-slate-400'}`}>
                                {formatDateTime(msg.createdAt)}
                              </div>
                              {msg.canEdit ? (
                                <button
                                  type="button"
                                  onClick={() => startEdit(msg)}
                                  className="rounded p-1 opacity-0 transition-opacity hover:bg-white/20 group-hover:opacity-100"
                                  title="ویرایش پیام"
                                >
                                  <Pencil className="h-3 w-3" />
                                </button>
                              ) : null}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="border-t border-slate-100 p-4">
                <div className="flex gap-2">
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        void sendReply();
                      }
                    }}
                    rows={2}
                    placeholder="پاسخ خود را بنویسید…"
                    className="min-h-[3rem] flex-1 resize-none rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  />
                  <button
                    type="button"
                    onClick={() => void sendReply()}
                    disabled={sending || !replyText.trim()}
                    className="flex h-10 w-10 shrink-0 items-center justify-center self-end rounded-xl bg-emerald-600 text-white transition-colors hover:bg-emerald-700 disabled:opacity-50"
                  >
                    <Send className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
